import type { RedoOnboardingV2RpcClient } from "@redotech/redo-onboarding-api/client";
import { createRedoOnboardingV2RpcClient } from "@redotech/redo-onboarding-api/client";
import type { saveProgress } from "@redotech/redo-onboarding-api/schema/save-progress";
import { Context, createContext, ReactNode, useMemo } from "react";
import type * as z from "zod";
import { useReadAnalyticsSessionId } from "./core/analytics-session";

type SaveProgressFn = RedoOnboardingV2RpcClient["saveProgress"];

export interface SaveProgressRpc {
  saveProgress(
    input: z.input<typeof saveProgress.input>,
  ): Promise<z.infer<typeof saveProgress.output>>;
}

export const OnboardingV2RpcClientContext: Context<
  RedoOnboardingV2RpcClient | undefined
> = createContext<RedoOnboardingV2RpcClient | undefined>(undefined);

OnboardingV2RpcClientContext.displayName = "OnboardingV2RpcClientContext";

export interface OnboardingV2RpcClientProviderProps {
  children: ReactNode | ReactNode[];
  baseUrl: string;
  authToken?: string;
}

export function OnboardingV2RpcClientProvider({
  children,
  baseUrl,
  authToken,
}: OnboardingV2RpcClientProviderProps) {
  const readAnalyticsSessionId = useReadAnalyticsSessionId();

  const client = useMemo(() => {
    if (!authToken) {
      return undefined;
    }

    const client = createRedoOnboardingV2RpcClient({
      baseURL: new URL(`${baseUrl}/onboarding-v2-rpc`),
      headers: { Authorization: authToken },
    });

    const saveProgress = client.saveProgress.bind(client) as SaveProgressFn;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    client.saveProgress = ((...args: Parameters<SaveProgressFn>) => {
      const [input, options] = args;
      const analyticsSessionId =
        input.analyticsSessionId ?? readAnalyticsSessionId();

      return saveProgress(
        { ...input, ...(analyticsSessionId ? { analyticsSessionId } : {}) },
        options,
      );
    }) as SaveProgressFn;

    return client;
  }, [baseUrl, authToken, readAnalyticsSessionId]);

  return (
    <OnboardingV2RpcClientContext.Provider value={client}>
      {children}
    </OnboardingV2RpcClientContext.Provider>
  );
}
